import { ReactNode } from 'react';

import { IconApi } from '@components/icons/api';
import { IconCompany } from '@components/icons/company';
import { IconIntegrations } from '@components/icons/integrations';
import { IconLock } from '@components/icons/lock';
import { IconMarketplace } from '@components/icons/marketplace';
import { IconPerson } from '@components/icons/person';
import { IconPortal } from '@components/icons/portal';
import { IconSSO } from '@components/icons/sso';
import { IconStatistics } from '@components/icons/statistics';
import { IconTeam } from '@components/icons/team';
import { getScopedTranslate } from '@locale/translate';

import { usePermissions } from 'contexts/permissions-context';

import { MyDetails } from './pages/account/my-details';
import { MySecurity } from './pages/account/security';
import { UserApps } from './pages/account/user-apps';
import { AppMarketplaceDetail } from './pages/integrations/app-marketplace/details';
import { AppMarketplace } from './pages/integrations/app-marketplace/list';
import { IntegrationDetail } from './pages/integrations/detail';
import { Integrations } from './pages/integrations/list';
import { EmailDomain } from './pages/organization/email-domains';
import { General } from './pages/organization/general';
import { MCPServers } from './pages/organization/mcp-servers';
import { MCPServerDetail } from './pages/organization/mcp-servers/detail';
import { Security } from './pages/organization/security';
import { TeamList } from './pages/organization/user-list';
import { PortalDetails } from './pages/portals/portal-layout';
import { PortalStatistics } from './pages/portals/portal-statistics';
import { Portals } from './pages/portals/portals';
import { BillingPage } from './pages/subscription/billing';
import { Plans } from './pages/subscription/plans';

import { AccessDenied } from '../access-denied';

const locale = {
  en: {
    settings: 'Settings',
    members: 'Members',
    companySecurity: 'Organization security',
    profileSettings: 'Profile settings',
    accountSecuritySettings: 'Account security settings',
    apiSettings: 'API settings',
    integrations: 'Integrations',
    appMarketplace: 'App marketplace',
    portals: 'Portals',
    statistics: 'Statistics',
    mcpServers: 'MCP Servers',
    emailDomain: 'Email domain',
    plans: 'Plans',
    billing: 'Billing',
    integration: 'Integration',
    portal: 'Portal',
    mcpServer: 'MCP server',
  },
};

const { t } = getScopedTranslate(locale);

const AdminGuard = ({ children }: { children: ReactNode }) => {
  const { canManageCompany } = usePermissions();

  if (!canManageCompany) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

const AdminOrEditorGuard = ({ children }: { children: ReactNode }) => {
  const { canViewSettings } = usePermissions();

  if (!canViewSettings) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

export const settingsPrefix = '/settings/';

export const settingsRoutes = [
  {
    commandPaletteName: t('settings'),
    commandPaletteIcon: IconCompany,
    name: 'settingsGeneral',
    path: 'general',
    page: (
      <AdminGuard>
        <General />
      </AdminGuard>
    ),
  },
  {
    pageTitle: t('emailDomain'),
    name: 'settingsEmailDomain',
    path: 'email-domain',
    page: (
      <AdminOrEditorGuard>
        <EmailDomain />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('plans'),
    name: 'subscriptionPlans',
    path: 'plans',
    page: (
      <AdminGuard>
        <Plans />
      </AdminGuard>
    ),
  },
  {
    pageTitle: t('billing'),
    name: 'subscriptionBilling',
    path: 'billing/*',
    page: (
      <AdminGuard>
        <BillingPage />
      </AdminGuard>
    ),
  },
  {
    commandPaletteName: t('members'),
    commandPaletteIcon: IconTeam,
    name: 'settingsUsers',
    path: 'users/*',
    page: (
      <AdminGuard>
        <TeamList />
      </AdminGuard>
    ),
  },
  {
    commandPaletteName: t('companySecurity'),
    commandPaletteIcon: IconSSO,
    name: 'settingsOrganizationSecurity',
    path: 'organization-security',
    page: (
      <AdminOrEditorGuard>
        <Security />
      </AdminOrEditorGuard>
    ),
  },
  {
    commandPaletteName: t('profileSettings'),
    commandPaletteIcon: IconPerson,
    name: 'settingsProfile',
    path: 'profile',
    page: <MyDetails />,
  },
  {
    commandPaletteName: t('accountSecuritySettings'),
    commandPaletteIcon: IconLock,
    name: 'settingsAccountSecurity',
    path: 'account-security',
    page: <MySecurity />,
  },
  {
    commandPaletteName: t('apiSettings'),
    commandPaletteIcon: IconApi,
    name: 'settingsApi',
    path: 'api',
    page: (
      <AdminOrEditorGuard>
        <UserApps />
      </AdminOrEditorGuard>
    ),
  },
  {
    commandPaletteName: t('appMarketplace'),
    commandPaletteIcon: IconMarketplace,
    name: 'appMarketplace',
    path: 'app-marketplace',
    page: <AppMarketplace />,
  },
  {
    pageTitle: t('appMarketplace'),
    name: 'appMarketplaceDetail',
    path: 'app-marketplace/:templateId/details',
    page: <AppMarketplaceDetail />,
  },
  {
    commandPaletteName: t('integrations'),
    commandPaletteIcon: IconIntegrations,
    name: 'integrations',
    path: 'integrations',
    page: (
      <AdminOrEditorGuard>
        <Integrations />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('integration'),
    name: 'integrationDetail',
    path: 'integration/:integrationId/*',
    page: (
      <AdminOrEditorGuard>
        <IntegrationDetail />
      </AdminOrEditorGuard>
    ),
  },
  {
    commandPaletteName: t('portals'),
    commandPaletteIcon: IconPortal,
    name: 'portals',
    path: 'portals',
    page: (
      <AdminOrEditorGuard>
        <Portals />
      </AdminOrEditorGuard>
    ),
  },
  {
    commandPaletteName: t('statistics'),
    commandPaletteIcon: IconStatistics,
    name: 'portalStatistics',
    path: 'portals-statistics',
    page: (
      <AdminOrEditorGuard>
        <PortalStatistics />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('portal'),
    name: 'portalDetail',
    path: 'portals/:portalId/*',
    page: (
      <AdminOrEditorGuard>
        <PortalDetails />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('mcpServers'),
    name: 'mcpServers',
    path: 'mcp-servers',
    page: (
      <AdminOrEditorGuard>
        <MCPServers />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('mcpServer'),
    name: 'mcpServerDetail',
    path: 'mcp-servers/:serverId',
    page: (
      <AdminOrEditorGuard>
        <MCPServerDetail />
      </AdminOrEditorGuard>
    ),
  },
] as const;
